// Properties panel — two-state, 1:1 with the live editor (snapshot L1051-1450).
//
// NOTHING selected  -> LABEL properties: background color, background image
//   (add/opacity/lock/remove), rounded-corners toggle, grid + snap toggles +
//   grid size, and info stats (object count, orientation).
// OBJECT selected    -> sections built from `.prop-label` headings + `.num-stepper`
//   numerics: Position & Size, Rotation + Flip/Lock, then per-type Font/Styling,
//   Fill & Stroke, Appearance (opacity + shadow), Barcode, QR, Image, Layer Order.
//
// Test contract (smoke.mjs): `#propertiesPanel .prop-row` count >= 5 when an
// object is selected, and the FIRST `#propertiesPanel input[type=color]` is the
// Fill swatch (changing it changes obj.fill). Both are honoured below: every
// labelled control is wrapped in a `.prop-row`, and in object mode the Fill &
// Stroke section (Fill color first) is emitted before any other color input.
//
// The panel builds its own inner shell (title header + scroll body) because
// `#propertiesPanel` is an empty aside in v2.
import { Shadow, filters as fabricFilters, FabricImage, Rect } from 'fabric'
import type { Canvas, FabricObject } from 'fabric'
import { getCanvas, state } from './../state'
import { labelMm, labelPx, refreshGrid, applyCanvasShape } from './../canvas'
import { MM_PER_IN } from './../units'
import { saveState } from './../history'
import { toggleBold, toggleItalic, toggleUnderline, setTextAlign, applyFont, isTextObj } from './../widgets/text'
import { fitTextToWidth, setAutoFitBaseSize } from './../widgets/autofit'
import { textFitOf, setTextFit, setBoxHeight, setVerticalAlign } from './../widgets/textbox-fit'
const isTextbox = (o: { type?: string }) => (o.type ?? '').toLowerCase() === 'textbox'
import { updateLayers } from './layers'
import { updateBarcode } from './../widgets/barcode'
import { updateQRValue } from './../widgets/qrcode'
import { openSizePicker } from './sizePicker'
import { el, numStepper, toast } from './../ui/dom'
import { icon } from './../ui/icons'
// fontPicker.ts is owned by another agent and may still be a stub at build time;
// import as a namespace + runtime-guard so this file always compiles + runs.
import * as FontPicker from './fontPicker'
import {
  isTable, getTableModel, getTableStyle, setCellFill, setCellStroke, setBorderWidth,
  addRow, removeRow, addColumn, removeColumn, setCell as tableSetCell,
  getTableTextStyle, setCellTextAlign, setCellVAlign, setCellFontSize, setCellTextColor,
  setCellBorderStyle, toggleCellBold, toggleCellItalic, type BorderStyle,
} from './../widgets/table-widget'
import type { Table } from './../widgets/table'
import type { DataTable } from './../widgets/datatable'
import { renderDataTablePanel } from './datatable-panel'
import { convertDataRegion } from './../widgets/datatable'
import { isDynamicTable } from './../widgets/dynamic-table'
import { renderDynamicTablePanel } from './dynamic-table-panel'
import { openLiquidHelp } from './liquid-help'
import { openFormulaBuilder } from './formula-builder'

type AnyObj = FabricObject & Record<string, any>

function host(): HTMLElement | null {
  return document.getElementById('propertiesPanel')
}

// ── tiny builders (themed with safelisted utilities + named marker classes) ──

/** Section heading — live uses `.prop-label` divs as section labels. */
function heading(text: string): HTMLElement {
  return el('div', 'prop-label', text)
}

/** A labelled control row (carries `.prop-row` — tests count these). */
function row(label: string, control: HTMLElement): HTMLElement {
  const r = el('label', 'prop-row flex items-center gap-2')
  const l = el('span', 'prop-label text-[10px] text-slate-500')
  l.textContent = label
  l.style.minWidth = '46px'
  r.append(l, control)
  return r
}

/** A two-column block (label above control), as in the live X/Y/W/H grid. */
function field(label: string, control: HTMLElement): HTMLElement {
  const wrap = el('div')
  const l = el('label', 'text-[10px] text-slate-500')
  l.textContent = label
  wrap.append(l, control)
  return wrap
}

function colorInput(value: string, onInput: (v: string) => void, onCommit: () => void): HTMLInputElement {
  const i = el('input') as HTMLInputElement
  i.type = 'color'
  i.value = value && value.startsWith('#') ? value : '#000000'
  i.style.width = '32px'; i.style.height = '32px'
  i.className = 'rounded border border-slate-600 cursor-pointer'
  i.style.background = 'transparent'
  i.addEventListener('input', () => onInput(i.value))
  i.addEventListener('change', onCommit)
  return i
}

function textInput(value: string, placeholder: string, onCommit: (v: string) => void): HTMLInputElement {
  const i = el('input', 'prop-input') as HTMLInputElement
  i.type = 'text'
  i.value = value
  i.placeholder = placeholder
  i.addEventListener('change', () => onCommit(i.value))
  return i
}

function select(options: [string, string][], value: string, onChange: (v: string) => void): HTMLSelectElement {
  const s = el('select', 'prop-input flex-1') as HTMLSelectElement
  for (const [val, lbl] of options) {
    const o = el('option') as HTMLOptionElement
    o.value = val; o.textContent = lbl
    if (val === value) o.selected = true
    s.appendChild(o)
  }
  s.addEventListener('change', () => onChange(s.value))
  return s
}

function checkbox(label: string, checked: boolean, onChange: (v: boolean) => void): HTMLElement {
  const wrap = el('label', 'flex items-center gap-2 cursor-pointer')
  const i = el('input') as HTMLInputElement
  i.type = 'checkbox'
  i.checked = checked
  i.style.accentColor = '#3b82f6'
  i.addEventListener('change', () => onChange(i.checked))
  const span = el('span', 'text-[11px] text-slate-400', label)
  wrap.append(i, span)
  return wrap
}

function range(value: number, min: number, max: number, step: number, onInput: (v: number) => void, onCommit: () => void): HTMLInputElement {
  const i = el('input', 'flex-1') as HTMLInputElement
  i.type = 'range'
  i.min = String(min); i.max = String(max); i.step = String(step)
  i.value = String(value)
  i.addEventListener('input', () => onInput(parseFloat(i.value)))
  i.addEventListener('change', onCommit)
  return i
}

/** A `.prop-icon-btn` toggle/action button (icon-only, like the live panel). */
function iconBtn(iconName: string, title: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'prop-icon-btn' + (active ? ' active' : '')) as HTMLButtonElement
  b.type = 'button'
  b.title = title
  b.innerHTML = icon(iconName, 'w-3.5 h-3.5')
  b.addEventListener('click', onClick)
  return b
}

/** A small text-label toggle button styled like the live `.prop-icon-btn` for B/I/U. */
function textToggleBtn(label: string, title: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'prop-icon-btn' + (active ? ' active' : '')) as HTMLButtonElement
  b.type = 'button'
  b.title = title
  b.textContent = label
  b.style.fontWeight = '700'
  b.addEventListener('click', onClick)
  return b
}

function btnRow(...children: HTMLElement[]): HTMLElement {
  const d = el('div', 'flex gap-1')
  d.append(...children)
  return d
}

/** A small flex-fill text button matching the live Layer Order buttons. */
function smallBtn(label: string, title: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'tb-btn flex-1 justify-center text-[11px]') as HTMLButtonElement
  b.type = 'button'
  b.title = title
  b.textContent = label
  b.addEventListener('click', onClick)
  return b
}

function block(...children: (HTMLElement | null)[]): HTMLElement {
  const d = el('div', 'space-y-2')
  for (const c of children) if (c) d.append(c)
  return d
}

// ── shell ──

function shell(titleText: string): { body: HTMLElement } {
  const h = host()!
  h.replaceChildren()
  const header = el('div', 'px-3 pt-2.5 pb-2 flex items-center border-b border-slate-800 shrink-0')
  const title = el('span', 'text-[11px] font-semibold text-slate-300 uppercase tracking-wide', titleText)
  header.append(title)
  const scroll = el('div', 'flex-1 overflow-y-auto overflow-x-hidden')
  const body = el('div', 'px-3 pt-2 pb-6 space-y-4')
  scroll.append(body)
  h.append(header, scroll)
  return { body }
}

// ── main entry ──

export function onSelectionChange(): void {
  if (!host()) return
  const canvas: Canvas = getCanvas()
  const obj = canvas.getActiveObject() as AnyObj | undefined
  if (!obj) {
    renderLabelProps(canvas)
    return
  }
  renderObjectProps(canvas, obj)
}

// ── LABEL props (nothing selected) ──────────────────────────────────────────

function renderLabelProps(canvas: Canvas): void {
  const { body } = shell('Label Properties')
  const size = state.currentSize

  // Label info
  const info = el('div')
  info.append(heading('Label'))
  const infoBody = el('div', 'space-y-2')
  const nameRow = el('div', 'flex items-center gap-2')
  const ico = el('span', 'text-slate-500 shrink-0')
  ico.innerHTML = icon('square', 'w-4 h-4')
  const nameSpan = el('span', 'text-[12px] text-slate-300 truncate', size?.name ?? 'Custom')
  nameRow.append(ico, nameSpan)
  // Display dims honour orientation (a portrait A4 reads 210×297, not the
  // landscape-canonical stored dims).
  const disp = labelMm()
  const dims = el('div', 'grid grid-cols-2 gap-2')
  dims.append(
    field('Width', el('div', 'text-[12px] text-slate-300', size ? `${disp.w} mm · ${(disp.w / MM_PER_IN).toFixed(2)} in` : '—')),
    field('Height', el('div', 'text-[12px] text-slate-300', size ? `${disp.h} mm · ${(disp.h / MM_PER_IN).toFixed(2)} in` : '—')),
  )
  const change = el('button', 'text-[11px] text-blue-400 hover:text-blue-300', 'Change size…')
  change.type = 'button'
  change.addEventListener('click', () => openSizePicker())
  infoBody.append(nameRow, dims, change)
  info.append(infoBody)

  // Background color
  const bg = el('div')
  bg.append(heading('Background'))
  const bgRow = el('div', 'flex items-center gap-2')
  const bgCurrent = (canvas.backgroundColor as string) || '#ffffff'
  const bgColor = el('input') as HTMLInputElement
  bgColor.type = 'color'
  bgColor.value = bgCurrent.startsWith('#') ? bgCurrent : '#ffffff'
  bgColor.style.width = '32px'; bgColor.style.height = '32px'
  bgColor.className = 'rounded border border-slate-600 cursor-pointer'
  bgColor.style.background = 'transparent'
  const bgLabel = el('span', 'text-[11px] text-slate-400', bgColor.value)
  bgLabel.style.fontFamily = 'monospace'
  const reset = el('button', 'text-[10px] text-slate-400 hover:text-white', 'Reset')
  reset.type = 'button'
  reset.style.marginLeft = 'auto'
  const applyBg = (v: string) => {
    canvas.backgroundColor = v
    bgColor.value = v.startsWith('#') ? v : '#ffffff'
    bgLabel.textContent = bgColor.value
    canvas.requestRenderAll()
  }
  bgColor.addEventListener('input', () => applyBg(bgColor.value))
  bgColor.addEventListener('change', () => saveState())
  reset.addEventListener('click', () => { applyBg('#ffffff'); saveState() })
  bgRow.append(bgColor, bgLabel, reset)
  bg.append(bgRow)

  // Background image
  const bgImg = el('div')
  bgImg.append(heading('Background Image'))
  bgImg.append(renderBgImageControls(canvas))

  // Rounded corners
  const shapeSec = el('div')
  shapeSec.append(heading('Label Shape'))
  shapeSec.append(checkbox('Rounded corners', state.roundedCorners, (v) => {
    state.roundedCorners = v
    applyCanvasShape()
    toast(v ? 'Rounded corners on' : 'Rounded corners off')
  }))

  // Grid & snap
  const gridSec = el('div')
  gridSec.append(heading('Grid'))
  const gridBody = el('div', 'space-y-2')
  const gridTopRow = el('div', 'flex items-center gap-2')
  gridTopRow.append(checkbox('Show grid', state.gridEnabled, (v) => { state.gridEnabled = v; refreshGrid() }))
  const sizeWrap = el('div', 'flex items-center gap-1')
  sizeWrap.style.marginLeft = 'auto'
  sizeWrap.append(el('span', 'text-[10px] text-slate-500', 'Size'))
  const gridSizeSel = select(
    [['1', '1 mm'], ['2', '2 mm'], ['5', '5 mm'], ['10', '10 mm']],
    String(state.gridSize),
    (v) => { state.gridSize = parseFloat(v) || 5; refreshGrid() },
  )
  gridSizeSel.classList.remove('flex-1')
  sizeWrap.append(gridSizeSel)
  gridTopRow.append(sizeWrap)
  gridBody.append(gridTopRow)
  gridBody.append(checkbox('Snap to grid', state.snapToGrid, (v) => { state.snapToGrid = v }))
  gridSec.append(gridBody)

  // Info stats
  const statsSec = el('div')
  statsSec.append(heading('Info'))
  const statsBody = el('div', 'space-y-1')
  const objects = canvas.getObjects().filter((o) => !(o as any).isGrid && !(o as any).isBgImage).length
  const statRow = (label: string, value: string) => {
    const r = el('div', 'flex justify-between text-[11px]')
    r.append(el('span', 'text-slate-500', label), el('span', 'text-slate-400', value))
    return r
  }
  statsBody.append(
    statRow('Objects', String(objects)),
    statRow('Orientation', state.orientation === 'portrait' ? 'Portrait' : 'Landscape'),
  )
  statsSec.append(statsBody)

  body.append(info, bg, bgImg, shapeSec, gridSec, statsSec)
}

function findBgImage(canvas: Canvas): AnyObj | undefined {
  return canvas.getObjects().find((o) => (o as any).isBgImage) as AnyObj | undefined
}

function renderBgImageControls(canvas: Canvas): HTMLElement {
  const wrap = el('div')
  const existing = findBgImage(canvas)

  // Hidden file input shared by add/replace.
  const fileInput = el('input') as HTMLInputElement
  fileInput.type = 'file'
  fileInput.accept = 'image/*'
  fileInput.style.display = 'none'
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      const dataUrl = reader.result as string
      const prev = findBgImage(canvas)
      if (prev) canvas.remove(prev)
      const img = await FabricImage.fromURL(dataUrl)
      const { w: cw, h: ch } = labelPx() // label scene px (the element also holds the pasteboard)
      img.set({
        left: 0, top: 0,
        scaleX: cw / (img.width || cw),
        scaleY: ch / (img.height || ch),
        opacity: 0.3, selectable: false, evented: false,
        isBgImage: true, bgImageLocked: true,
      } as any)
      canvas.add(img)
      canvas.sendObjectToBack(img)
      canvas.requestRenderAll()
      saveState()
      onSelectionChange()
    }
    reader.readAsDataURL(file)
  })
  wrap.append(fileInput)

  if (!existing) {
    const add = el('button', 'w-full rounded-lg text-[11px] text-slate-400 hover:text-slate-300', '+ Add background image')
    add.type = 'button'
    add.style.padding = '8px 0'
    add.style.border = '1px dashed #475569'
    add.addEventListener('click', () => fileInput.click())
    wrap.append(add)
    return wrap
  }

  const set = el('div', 'space-y-2')
  const r = el('div', 'flex items-center gap-2')
  const thumb = el('div', 'rounded border border-slate-600 bg-slate-800 shrink-0')
  thumb.style.width = '40px'; thumb.style.height = '40px'
  thumb.style.backgroundSize = 'cover'; thumb.style.backgroundPosition = 'center'
  const src = (existing as any).getSrc?.() ?? (existing as any)._element?.src
  if (src) thumb.style.backgroundImage = `url(${src})`
  const mid = el('div', 'flex-1 min-w-0')
  mid.style.minWidth = '0'
  const acts = el('div', 'flex gap-2 mt-1')
  const locked = (existing as any).bgImageLocked !== false
  const lockBtn = el('button', 'text-[10px] text-slate-400 hover:text-white', locked ? 'Locked' : 'Unlocked')
  lockBtn.type = 'button'
  lockBtn.addEventListener('click', () => {
    const nowLocked = (existing as any).bgImageLocked === false
    ;(existing as any).bgImageLocked = nowLocked
    existing.set({ selectable: !nowLocked, evented: !nowLocked } as any)
    canvas.requestRenderAll(); saveState(); onSelectionChange()
  })
  const replace = el('button', 'text-[10px] text-slate-400 hover:text-white', 'Replace')
  replace.type = 'button'
  replace.addEventListener('click', () => fileInput.click())
  const remove = el('button', 'text-[10px] text-red-400 hover:text-red-300', 'Remove')
  remove.type = 'button'
  remove.addEventListener('click', () => { canvas.remove(existing); canvas.requestRenderAll(); saveState(); onSelectionChange() })
  acts.append(lockBtn, replace, remove)
  mid.append(acts)
  const opWrap = el('div', 'flex flex-col gap-1')
  opWrap.append(el('label', 'text-[9px] text-slate-500', 'Opacity'))
  const op = el('input') as HTMLInputElement
  op.type = 'range'; op.min = '0.05'; op.max = '1'; op.step = '0.05'
  op.value = String(existing.opacity ?? 0.3)
  op.style.width = '56px'
  op.addEventListener('input', () => { existing.set({ opacity: parseFloat(op.value) } as any); canvas.requestRenderAll() })
  op.addEventListener('change', () => saveState())
  opWrap.append(op)
  r.append(thumb, mid, opWrap)
  set.append(r)
  wrap.append(set)
  return wrap
}

// ── OBJECT props (selection active) ─────────────────────────────────────────

function renderObjectProps(canvas: Canvas, obj: AnyObj): void {
  const isText = isTextObj(obj)
  const isBarcode = obj.barcodeFormat != null
  const isQR = obj.qrValue != null
  const isImage = obj.type === 'image' && !isQR
  const tableSel = obj.type === 'Table'
  const dynTableSel = isDynamicTable(obj)
  const dataTableSel = !dynTableSel && obj.type?.toLowerCase() === 'datatable'
  const dataRegionSel = !!(obj as any).olmDataRegion
  const isGroup = obj.type === 'group' || obj.type === 'activeselection'
  const showFillStroke = !tableSel && !dynTableSel && !dataTableSel && !dataRegionSel && !isBarcode && !isQR && !isImage && (isText || !isGroup)

  let title = 'Properties'
  if (dataRegionSel) title = 'Data Region'
  else if (dynTableSel) title = 'Dynamic Table'
  else if (dataTableSel) title = 'Data Table'
  else if (tableSel) title = 'Table'
  else if (isBarcode) title = 'Barcode'
  else if (isQR) title = 'QR Code'
  else if (isText) title = 'Text'
  else if (isImage) title = 'Image'
  else if (obj.type === 'activeselection') title = 'Selection'
  else title = obj.type ? obj.type[0].toUpperCase() + obj.type.slice(1) : 'Object'

  const { body } = shell(title + ' Properties')
  const render = () => canvas.requestRenderAll()
  const commit = () => { saveState(); updateLayers() }

  body.append(renderPositionSection(canvas, obj, render, commit))

  if (dataRegionSel) {
    body.append(renderConvertRegion(canvas, obj, commit))
    body.append(renderLayerOrder(canvas, obj, commit))
    return
  }
  if (dataTableSel) {
    renderDataTablePanel(body, obj as unknown as DataTable, () => { render(); commit() }, onSelectionChange)
    body.append(renderLayerOrder(canvas, obj, commit))
    return
  }
  if (isDynamicTable(obj)) {
    renderDynamicTablePanel(body, obj, render, commit)
    renderTableSection(body, obj, render, commit) // shared structure + cell styling
    body.append(renderLayerOrder(canvas, obj, commit))
    return
  }
  if (isTable(obj)) {
    renderTableSection(body, obj, render, commit)
    body.append(renderLayerOrder(canvas, obj, commit))
    return
  }

  if (isText) body.append(renderTextSection(obj, render, commit))
  if (showFillStroke) body.append(renderFillStrokeSection(obj, render, commit))
  body.append(renderAppearanceSection(obj, render, commit))
  if (isBarcode) body.append(renderBarcodeSection(obj, commit))
  if (isQR) body.append(renderQRSection(obj, commit))
  if (isImage) body.append(renderImageSection(canvas, obj, render, commit))
  body.append(renderLayerOrder(canvas, obj, commit))
}

function renderPositionSection(canvas: Canvas, obj: AnyObj, render: () => void, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Position & Size'))
  const grid = el('div', 'grid grid-cols-2 gap-2')
  grid.append(
    field('X', wrapRow(numStepper(Math.round(obj.left || 0), (v) => { obj.set({ left: v } as any); obj.setCoords(); render() }, { step: 1 }), commit)),
    field('Y', wrapRow(numStepper(Math.round(obj.top || 0), (v) => { obj.set({ top: v } as any); obj.setCoords(); render() }, { step: 1 }), commit)),
    // Text boxes: W/H set the box (wrap width / box height) — scaling would
    // stretch the glyphs (customer #8). Everything else scales as before.
    field('W', wrapRow(numStepper(Math.round(obj.getScaledWidth()), (v) => {
      if (isTextbox(obj)) { obj.set({ width: Math.max(1, v / (obj.scaleX || 1)) } as any); (obj as any).initDimensions?.(); fitTextToWidth(obj); obj.setCoords(); render() }
      else if (obj.width) { obj.set({ scaleX: v / obj.width } as any); obj.setCoords(); render() }
    }, { min: 1, step: 1 }), commit)),
    field('H', wrapRow(numStepper(Math.round(obj.getScaledHeight()), (v) => {
      if (isTextbox(obj)) { setBoxHeight(obj, Math.max(1, v / (obj.scaleY || 1))); fitTextToWidth(obj); render() }
      else if (obj.height) { obj.set({ scaleY: v / obj.height } as any); obj.setCoords(); render() }
    }, { min: 1, step: 1 }), commit)),
  )
  sec.append(grid)

  const rotRow = el('div', 'mt-2 flex gap-2')
  const rotField = el('div', 'flex-1')
  rotField.append(field('Rotation °', wrapRow(numStepper(Math.round(obj.angle || 0), (v) => { obj.set({ angle: v } as any); obj.setCoords(); render() }, { min: -360, max: 360, step: 1 }), commit)))
  const flipField = el('div')
  flipField.append(el('label', 'text-[10px] text-slate-500', 'Flip'))
  const locked = obj.lockMovementX === true
  flipField.append(btnRow(
    iconBtn('flip', 'Flip horizontal', false, () => { obj.set({ flipX: !obj.flipX } as any); render(); commit() }),
    iconBtn('flip', 'Flip vertical', false, () => { obj.set({ flipY: !obj.flipY } as any); render(); commit() }),
    iconBtn(locked ? 'lock' : 'unlock', 'Lock object', locked, () => {
      const nowLocked = obj.lockMovementX !== true
      obj.set({
        lockMovementX: nowLocked, lockMovementY: nowLocked, lockScalingX: nowLocked,
        lockScalingY: nowLocked, lockRotation: nowLocked, hasControls: !nowLocked,
      } as any)
      render(); commit(); onSelectionChange()
    }),
  ))
  rotRow.append(rotField, flipField)
  sec.append(rotRow)
  return sec
}

/** Wrap a numStepper so its `.prop-row` ancestry is counted by tests. */
function wrapRow(stepper: HTMLElement, commit: () => void): HTMLElement {
  // numStepper's input fires onChange on every step; treat any change as a commit.
  const input = stepper.querySelector('input')
  input?.addEventListener('change', commit)
  stepper.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => setTimeout(commit, 0)))
  stepper.classList.add('prop-row')
  return stepper
}

function renderTextSection(obj: AnyObj, render: () => void, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Font & Styling'))
  const body = el('div', 'space-y-2')

  // Font family — via mountFontPicker when implemented; plain input fallback.
  const fontHost = el('div', 'relative')
  const mount = (FontPicker as any).mountFontPicker as
    | ((h: HTMLElement, o?: { value?: string; onChange?: (f: string) => void }) => void)
    | undefined
  if (typeof mount === 'function') {
    mount(fontHost, { value: obj.fontFamily || 'Inter', onChange: (family: string) => { applyFont(family); render(); commit() } })
  } else {
    const inp = textInput(obj.fontFamily || 'Inter', 'Font family', (v) => { applyFont(v); render(); commit() })
    fontHost.append(inp)
  }
  body.append(fontHost)

  // Font size + B/I/U
  const sizeRow = el('div', 'flex gap-2')
  const sizeWrap = el('div', 'flex-1')
  sizeWrap.append(wrapRow(numStepper(obj._autoFitOrigSize || obj.fontSize || 20, (v) => { if (obj.autoFitWidth) setAutoFitBaseSize(obj, v); else obj.set({ fontSize: v } as any); render() }, { min: 6, max: 200, step: 1 }), commit))
  const biu = el('div', 'flex gap-1')
  biu.append(
    textToggleBtn('B', 'Bold', obj.fontWeight === 'bold' || obj.fontWeight === 700, () => { toggleBold(); commit(); onSelectionChange() }),
    textToggleBtn('I', 'Italic', obj.fontStyle === 'italic', () => { toggleItalic(); commit(); onSelectionChange() }),
    textToggleBtn('U', 'Underline', !!obj.underline, () => { toggleUnderline(); commit(); onSelectionChange() }),
  )
  sizeRow.append(sizeWrap, biu)
  body.append(sizeRow)

  // Line height + char spacing
  const lhRow = el('div', 'flex gap-2')
  const lh = el('div', 'flex-1')
  lh.append(field('Line height', wrapRow(numStepper(obj.lineHeight ?? 1.16, (v) => { obj.set({ lineHeight: v } as any); render() }, { min: 0.5, max: 4, step: 0.1 }), commit)))
  const cs = el('div', 'flex-1')
  cs.append(field('Spacing', wrapRow(numStepper(obj.charSpacing ?? 0, (v) => { obj.set({ charSpacing: v } as any); render() }, { min: -200, max: 1000, step: 10 }), commit)))
  lhRow.append(lh, cs)
  body.append(lhRow)

  // Fit to box (customer request): how a long value behaves in the box.
  const isBox = (obj.type ?? '').toLowerCase() === 'textbox'
  const restoreSize = () => { if (obj._autoFitOrigSize) { obj.set({ fontSize: obj._autoFitOrigSize } as any); (obj as any).initDimensions?.(); obj.setCoords() } }
  body.append(field('Text fit', select([
    ['wrap', 'Wrap to new lines'],
    ['shrink', 'Shrink to fit width (one line)'],
    ['wrap-shrink', 'Wrap, then shrink to fit box'],
  ], textFitOf(obj), (v) => {
    setTextFit(obj, v as 'wrap' | 'shrink' | 'wrap-shrink')
    restoreSize()
    fitTextToWidth(obj)
    render(); commit(); onSelectionChange()
  })))
  if (isBox) {
    const bhRow = el('div', 'flex gap-2')
    const bh = el('div', 'flex-1')
    bh.append(field('Box height', wrapRow(numStepper(Math.round(obj.boxHeight ?? obj.height ?? 0), (v) => { setBoxHeight(obj, v); fitTextToWidth(obj); render() }, { min: 1, max: 2000, step: 1 }), commit)))
    const va = el('div', 'flex-1')
    const cv = obj.verticalAlign || 'top'
    va.append(field('Vertical', btnRow(
      textToggleBtn('⤒', 'Align top', cv === 'top', () => { setVerticalAlign(obj, 'top'); render(); commit(); onSelectionChange() }),
      textToggleBtn('↕', 'Align middle', cv === 'middle', () => { setVerticalAlign(obj, 'middle'); render(); commit(); onSelectionChange() }),
      textToggleBtn('⤓', 'Align bottom', cv === 'bottom', () => { setVerticalAlign(obj, 'bottom'); render(); commit(); onSelectionChange() }),
    )))
    bhRow.append(bh, va)
    body.append(bhRow)
  }

  // Alignment
  const cur = obj.textAlign || 'left'
  body.append(btnRow(
    iconBtn('alignLeft', 'Align left', cur === 'left', () => { setTextAlign('left'); commit(); onSelectionChange() }),
    iconBtn('alignCenter', 'Align center', cur === 'center', () => { setTextAlign('center'); commit(); onSelectionChange() }),
    iconBtn('alignRight', 'Align right', cur === 'right', () => { setTextAlign('right'); commit(); onSelectionChange() }),
  ))

  sec.append(body)
  return sec
}

function renderFillStrokeSection(obj: AnyObj, render: () => void, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Fill & Stroke'))
  const body = el('div', 'space-y-2')

  // Fill — MUST be the FIRST input[type=color] in object props (test contract).
  const fillRow = el('div', 'flex items-center gap-2')
  const fillLabel = el('label', 'text-[10px] text-slate-500', 'Fill')
  fillLabel.style.width = '40px'
  const fillVal = typeof obj.fill === 'string' && obj.fill !== 'transparent' ? obj.fill : '#000000'
  const fill = colorInput(fillVal, (v) => { obj.set({ fill: v } as any); render() }, commit)
  const fillNone = el('button', 'text-[10px] text-slate-400 hover:text-white', 'None')
  fillNone.type = 'button'
  fillNone.addEventListener('click', () => { obj.set({ fill: 'transparent' } as any); fill.value = '#000000'; render(); commit() })
  fillRow.append(fillLabel, fill, fillNone)
  body.append(fillRow)

  // Stroke + width
  const strokeRow = el('div', 'flex items-center gap-2')
  const strokeLabel = el('label', 'text-[10px] text-slate-500', 'Stroke')
  strokeLabel.style.width = '40px'
  const strokeVal = typeof obj.stroke === 'string' ? obj.stroke : '#000000'
  const stroke = colorInput(strokeVal, (v) => { obj.set({ stroke: v } as any); render() }, commit)
  const sw = numStepper(obj.strokeWidth || 0, (v) => { obj.set({ strokeWidth: v } as any); render() }, { min: 0, max: 20, step: 1 })
  sw.classList.add('flex-1')
  sw.querySelector('input')?.addEventListener('change', commit)
  strokeRow.append(strokeLabel, stroke, sw)
  body.append(strokeRow)

  // Stroke style → strokeDashArray
  const styleRow = el('div', 'flex items-center gap-2')
  const styleLabel = el('label', 'text-[10px] text-slate-500', 'Style')
  styleLabel.style.width = '40px'
  styleRow.append(styleLabel, select(
    [['solid', 'Solid'], ['dashed', 'Dashed'], ['dotted', 'Dotted'], ['dashdot', 'Dash-dot'], ['longdash', 'Long dash']],
    dashToStyle(obj.strokeDashArray),
    (v) => { obj.set({ strokeDashArray: styleToDash(v, obj.strokeWidth || 1) } as any); render(); commit() },
  ))
  body.append(styleRow)

  sec.append(body)
  return sec
}

function styleToDash(style: string, w: number): number[] | null {
  const u = Math.max(1, w)
  switch (style) {
    case 'dashed': return [u * 4, u * 3]
    case 'dotted': return [u, u * 2]
    case 'dashdot': return [u * 4, u * 2, u, u * 2]
    case 'longdash': return [u * 8, u * 4]
    default: return null
  }
}
function dashToStyle(dash: number[] | null | undefined): string {
  if (!dash || dash.length === 0) return 'solid'
  if (dash.length >= 4) return 'dashdot'
  if (dash[0] >= dash[1] * 2) return 'longdash'
  if (dash[0] <= dash[1]) return 'dotted'
  return 'dashed'
}

function renderAppearanceSection(obj: AnyObj, render: () => void, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Appearance'))
  const body = el('div', 'space-y-2')

  // Opacity slider
  const opRow = el('div', 'flex items-center gap-2')
  const opLabel = el('label', 'text-[10px] text-slate-500', 'Opacity')
  opLabel.style.width = '40px'
  opRow.append(opLabel, range(obj.opacity ?? 1, 0, 1, 0.05, (v) => { obj.set({ opacity: v } as any); render() }, commit))
  body.append(opRow)

  // Shadow toggle + color + blur
  const shRow = el('div', 'flex items-center gap-2')
  const shLabel = el('label', 'text-[10px] text-slate-500', 'Shadow')
  shLabel.style.width = '40px'
  const hasShadow = !!obj.shadow
  const controls = el('div', 'flex items-center gap-2 flex-1' + (hasShadow ? '' : ' hidden'))
  const shColor = colorInput(shadowColor(obj), (v) => { applyShadow(obj, { color: v }); render() }, commit)
  shColor.style.width = '24px'; shColor.style.height = '24px'
  const shBlur = numStepper(shadowBlur(obj), (v) => { applyShadow(obj, { blur: v }); render() }, { min: 0, max: 30, step: 1 })
  shBlur.style.width = '56px'
  shBlur.querySelector('input')?.addEventListener('change', commit)
  controls.append(shColor, shBlur)
  const toggle = iconBtn('shadow', 'Toggle shadow', hasShadow, () => {
    if (obj.shadow) { obj.set({ shadow: null } as any) }
    else { obj.set({ shadow: new Shadow({ color: '#000000', blur: 4, offsetX: 2, offsetY: 2 }) } as any) }
    render(); commit(); onSelectionChange()
  })
  shRow.append(shLabel, toggle, controls)
  body.append(shRow)

  sec.append(body)
  return sec
}

function shadowColor(obj: AnyObj): string {
  const c = obj.shadow?.color
  return typeof c === 'string' && c.startsWith('#') ? c : '#000000'
}
function shadowBlur(obj: AnyObj): number {
  return obj.shadow?.blur ?? 4
}
function applyShadow(obj: AnyObj, patch: { color?: string; blur?: number }): void {
  const cur = obj.shadow
  obj.set({ shadow: new Shadow({
    color: patch.color ?? shadowColor(obj),
    blur: patch.blur ?? shadowBlur(obj),
    offsetX: cur?.offsetX ?? 2,
    offsetY: cur?.offsetY ?? 2,
  }) } as any)
}

function renderBarcodeSection(obj: AnyObj, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Value'))
  const valBody = el('div', 'space-y-2')
  valBody.append(textInput(obj.barcodeValue || '', 'Enter value...', (v) => {
    void updateBarcode(obj, { value: v }).then(() => { onSelectionChange(); commit() })
  }))
  sec.append(valBody)

  sec.append(heading('Text'))
  const grid = el('div', 'grid grid-cols-2 gap-2')
  const fontSel = select(
    [['monospace', 'Monospace'], ['OCR-B', 'OCR-B'], ['Inter', 'Inter'], ['Arial', 'Arial'], ['Roboto Mono', 'Roboto Mono'], ['Courier New', 'Courier New']],
    obj.barcodeStyleFont || 'monospace',
    (v) => { void updateBarcode(obj, { style: { font: v } }).then(() => { onSelectionChange(); commit() }) },
  )
  fontSel.classList.remove('flex-1'); fontSel.classList.add('w-full')
  const posSel = select(
    [['bottom', 'Below'], ['top', 'Above']],
    obj.barcodeStyleTextPos || 'bottom',
    (v) => { void updateBarcode(obj, { style: { textPosition: v as 'bottom' | 'top' } }).then(() => { onSelectionChange(); commit() }) },
  )
  posSel.classList.remove('flex-1'); posSel.classList.add('w-full')
  const sizeStep = numStepper(obj.barcodeStyleFontSize ?? 14, (v) => {
    void updateBarcode(obj, { style: { fontSize: v } }).then(() => { onSelectionChange(); commit() })
  }, { min: 6, max: 36, step: 1 })
  const showWrap = el('div', 'flex items-center')
  showWrap.style.height = '32px'
  const showCb = el('input') as HTMLInputElement
  showCb.type = 'checkbox'
  showCb.checked = obj.barcodeStyleShowText !== false
  showCb.style.accentColor = '#3b82f6'
  showCb.addEventListener('change', () => { void updateBarcode(obj, { style: { displayValue: showCb.checked } }).then(() => { onSelectionChange(); commit() }) })
  showWrap.append(showCb, el('span', 'text-[11px] text-slate-400 ml-2', 'Text'))
  grid.append(
    field('Font', fontSel),
    field('Position', posSel),
    field('Size', sizeStep),
    field('Show', showWrap),
  )
  sec.append(grid)
  return sec
}

function renderQRSection(obj: AnyObj, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Value'))
  const body = el('div', 'space-y-2')
  body.append(textInput(obj.qrValue || '', 'URL or text...', (v) => {
    void updateQRValue(obj, v).then(() => { onSelectionChange(); commit() })
  }))
  sec.append(body)
  return sec
}

const IMAGE_FILTERS: [string, string][] = [
  ['none', 'None'], ['grayscale', 'Grayscale'], ['sepia', 'Sepia'],
  ['brightness', 'Brighten'], ['contrast', 'High contrast'], ['blur', 'Blur'], ['invert', 'Invert'],
]

function makeFilter(kind: string): any {
  const f = fabricFilters as any
  switch (kind) {
    case 'grayscale': return new f.Grayscale()
    case 'sepia': return new f.Sepia()
    case 'brightness': return new f.Brightness({ brightness: 0.15 })
    case 'contrast': return new f.Contrast({ contrast: 0.25 })
    case 'blur': return new f.Blur({ blur: 0.1 })
    case 'invert': return new f.Invert()
    default: return null
  }
}

function renderImageSection(_canvas: Canvas, obj: AnyObj, render: () => void, commit: () => void): HTMLElement {
  const sec = el('div')
  const body = el('div', 'space-y-2')

  // Corner radius
  const radRow = el('div', 'flex items-center gap-2')
  const radLabel = el('label', 'text-[10px] text-slate-500', 'Radius')
  radLabel.style.width = '64px'
  const radStep = numStepper(obj._cornerRadius ?? 0, (v) => {
    const r = Math.max(0, v)
    ;(obj as any)._cornerRadius = r
    const w = obj.width || 0; const h = obj.height || 0
    if (r > 0) {
      obj.set({ clipPath: new Rect({ width: w, height: h, rx: r, ry: r, originX: 'center', originY: 'center' }) } as any)
    } else {
      obj.set({ clipPath: undefined } as any)
    }
    render()
  }, { min: 0, max: 9999, step: 2 })
  radStep.classList.add('flex-1')
  radStep.querySelector('input')?.addEventListener('change', commit)
  radRow.append(radLabel, radStep, el('span', 'text-[10px] text-slate-500', 'px'))
  body.append(radRow)

  // Filter
  const filRow = el('div', 'flex items-center gap-2')
  const filLabel = el('label', 'text-[10px] text-slate-500', 'Filter')
  filLabel.style.width = '64px'
  const cur = (obj as any)._filterKind || 'none'
  filRow.append(filLabel, select(IMAGE_FILTERS, cur, (v) => {
    ;(obj as any)._filterKind = v
    const f = makeFilter(v)
    ;(obj as any).filters = f ? [f] : []
    ;(obj as any).applyFilters?.()
    render(); commit()
  }))
  body.append(filRow)

  sec.append(body)
  return sec
}

/** AI-import data-region placeholder → one-click convert to a real DataTable. */
function renderConvertRegion(canvas: Canvas, obj: AnyObj, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Detected table'))
  const meta: any = (obj as any).olmDataRegion ?? {}
  const cols: any[] = Array.isArray(meta.columns) ? meta.columns : []
  sec.append(el('div', 'text-xs text-slate-400 mb-2', `AI import flagged this as a repeating table with ${cols.length} column${cols.length === 1 ? '' : 's'}.`))
  if (cols.length) {
    sec.append(el('div', 'text-[11px] text-slate-500 mb-3 break-words', cols.map((c) => c?.header ?? c?.field).filter(Boolean).join(' · ')))
  }
  const btn = el('button', 'tb-btn primary py-2 w-full justify-center')
  const ic = document.createElement('span'); ic.innerHTML = icon('dataTable', 'w-4 h-4'); btn.prepend(ic)
  btn.append(document.createTextNode('Convert to data table'))
  btn.addEventListener('click', () => {
    convertDataRegion(obj as unknown as FabricObject)
    commit()
    onSelectionChange() // re-render the panel for the new DataTable
  })
  sec.append(btn)
  sec.append(el('div', 'text-[11px] text-slate-500 mt-2', 'You can map columns, set formats and totals after converting.'))
  return sec
}

function renderLayerOrder(canvas: Canvas, obj: AnyObj, commit: () => void): HTMLElement {
  const sec = el('div')
  sec.append(heading('Layer Order'))
  sec.append(btnRow(
    smallBtn('Front', 'Bring to front', () => { canvas.bringObjectToFront(obj); canvas.requestRenderAll(); commit() }),
    smallBtn('↑', 'Bring forward', () => { canvas.bringObjectForward(obj); canvas.requestRenderAll(); commit() }),
    smallBtn('↓', 'Send backward', () => { canvas.sendObjectBackwards(obj); canvas.requestRenderAll(); commit() }),
    smallBtn('Back', 'Send to back', () => { canvas.sendObjectToBack(obj); canvas.requestRenderAll(); commit() }),
  ))
  return sec
}

// ── Table (Fabric Table group) — themed cell styling ─────────────────────────

function renderTableSection(body: HTMLElement, tbl: Table, render: () => void, commit: () => void): void {
  const model = getTableModel(tbl)
  const opsSec = el('div')
  opsSec.append(heading(`Table ${model.rows}×${model.cols}`))
  opsSec.append(btnRow(
    smallBtn('+ Row', 'Add row', () => { addRow(tbl); commit(); onSelectionChange() }),
    smallBtn('− Row', 'Remove row', () => { removeRow(tbl); commit(); onSelectionChange() }),
    smallBtn('+ Col', 'Add column', () => { addColumn(tbl); commit(); onSelectionChange() }),
    smallBtn('− Col', 'Remove column', () => { removeColumn(tbl); commit(); onSelectionChange() }),
  ))
  const insertIntoCell = (tpl: string): boolean => {
    const s = (tbl as any)._selectedCells?.[0] as { row: number; col: number } | undefined
    if (!s) return false
    tableSetCell(tbl, s.row, s.col, tpl)
    commit()
    return true
  }
  opsSec.append(btnRow(
    smallBtn('Σ Formula', 'Build a computed value (sum, currency, dates) without code',
      () => openFormulaBuilder(insertIntoCell, isDynamicTable(tbl) ? 'field' : 'summary')),
    smallBtn('ƒx Examples', 'Formatting & summary expression examples', () => openLiquidHelp(insertIntoCell)),
  ))
  body.append(opsSec)

  // Cell size — applies to the selected cell's column/row, or ALL when none.
  const selCell = () => (tbl as any)._selectedCells?.[0] as { row: number; col: number } | undefined
  const sizeSec = el('div')
  sizeSec.append(heading('Cell size'))
  const sizeBody = el('div', 'space-y-2')
  const wst = numStepper(Math.round(tbl.columnWidths[selCell()?.col ?? 0] ?? 100), (v) => {
    const s = selCell()
    if (s) tbl.setColumnWidth(s.col, v)
    else for (let c = 0; c < tbl.cols; c++) tbl.setColumnWidth(c, v)
    render()
  }, { min: 20, step: 5 })
  wst.querySelector('input')?.addEventListener('change', commit)
  sizeBody.append(row('Col width', wst))
  const hst = numStepper(Math.round(tbl.rowHeights[selCell()?.row ?? 0] ?? 26), (v) => {
    const s = selCell()
    if (s) tbl.setRowHeight(s.row, v)
    else for (let r = 0; r < tbl.rows; r++) tbl.setRowHeight(r, v)
    render()
  }, { min: 12, step: 2 })
  hst.querySelector('input')?.addEventListener('change', commit)
  sizeBody.append(row('Row height', hst))
  sizeBody.append(el('div', 'text-[10px] text-slate-500',
    'Applies to the selected cell’s column/row — or the whole table when no cell is selected. Tip: you can also drag cell borders on the canvas.'))
  sizeSec.append(sizeBody)
  body.append(sizeSec)

  // Field chips + summary hint (plain Table only — the DynamicTable panel has its own).
  if (!isDynamicTable(tbl) && state.batchHeaders?.length) {
    const fSec = el('div')
    fSec.append(heading('Insert field into selected cell'))
    const chips = el('div', 'flex flex-wrap gap-1.5')
    for (const h of state.batchHeaders) {
      const chip = el('button', 'batch-field-tag matched', `{{${h}}}`) as HTMLButtonElement
      chip.type = 'button'
      chip.addEventListener('click', () => {
        const s = selCell()
        if (!s) { toast('Click a cell in the table first'); return }
        tableSetCell(tbl, s.row, s.col, `{{${h}}}`)
        commit()
      })
      chips.append(chip)
    }
    fSec.append(chips)
    fSec.append(el('div', 'text-[10px] text-slate-500 mt-2',
      'Cells also take data summaries, e.g. {{ rows | sum: "amount" | money }} — evaluated when the document PDF is rendered.'))
    body.append(fSec)
  }

  const st = getTableStyle(tbl)
  const cellSec = el('div')
  cellSec.append(heading('Cell style'))
  const cellBody = el('div', 'space-y-2')
  cellBody.append(colorRowNone('Fill', st.fill, (c) => { setCellFill(tbl, c); render() }, commit))
  cellBody.append(colorRowNone('Border', st.stroke, (c) => { setCellStroke(tbl, c); render() }, commit))
  const bw = numStepper(st.strokeWidth ?? 1, (v) => { setBorderWidth(tbl, Math.max(0, v)); render() }, { min: 0, step: 1 })
  bw.querySelector('input')?.addEventListener('change', commit)
  cellBody.append(row('Border W', bw))
  cellBody.append(row('Border', select(
    [['solid', 'Solid'], ['dashed', 'Dashed'], ['dotted', 'Dotted']],
    getTableTextStyle(tbl).borderStyle,
    (v) => { setCellBorderStyle(tbl, v as BorderStyle); commit() },
  )))
  cellSec.append(cellBody)
  body.append(cellSec)

  const ts = getTableTextStyle(tbl)
  const txtSec = el('div')
  txtSec.append(heading(`Cell text · ${ts.scope === 'selection' ? 'selected' : 'all cells'}`))
  const txtBody = el('div', 'space-y-2')
  txtBody.append(btnRow(
    iconBtn('alignLeft', 'Align left', false, () => { setCellTextAlign(tbl, 'left'); commit() }),
    iconBtn('alignCenter', 'Align center', false, () => { setCellTextAlign(tbl, 'center'); commit() }),
    iconBtn('alignRight', 'Align right', false, () => { setCellTextAlign(tbl, 'right'); commit() }),
    textToggleBtn('B', 'Bold', false, () => { toggleCellBold(tbl); commit(); onSelectionChange() }),
    textToggleBtn('I', 'Italic', false, () => { toggleCellItalic(tbl); commit(); onSelectionChange() }),
  ))
  txtBody.append(btnRow(
    smallBtn('Top', 'Align top', () => { setCellVAlign(tbl, 'top'); commit() }),
    smallBtn('Middle', 'Align middle', () => { setCellVAlign(tbl, 'middle'); commit() }),
    smallBtn('Bottom', 'Align bottom', () => { setCellVAlign(tbl, 'bottom'); commit() }),
  ))
  const fs = numStepper(ts.fontSize, (v) => { setCellFontSize(tbl, v); render() }, { min: 4, step: 1 })
  fs.querySelector('input')?.addEventListener('change', commit)
  txtBody.append(row('Font size', fs))
  txtBody.append(row('Text', colorInput(ts.textColor.startsWith('#') ? ts.textColor : '#333333', (v) => { setCellTextColor(tbl, v); render() }, commit)))
  txtSec.append(txtBody)
  body.append(txtSec)
}

/** A color swatch + "none" checkbox row (table cells can be transparent). */
function colorRowNone(label: string, current: string, apply: (color: string) => void, commit: () => void): HTMLElement {
  const transparent = !current || current === 'transparent'
  const swatch = colorInput(current && current.startsWith('#') ? current : '#ffffff', (v) => apply(v), commit)
  swatch.disabled = transparent
  const noneCb = el('input') as HTMLInputElement
  noneCb.type = 'checkbox'
  noneCb.checked = transparent
  noneCb.style.accentColor = '#3b82f6'
  noneCb.addEventListener('change', () => {
    swatch.disabled = noneCb.checked
    apply(noneCb.checked ? 'transparent' : swatch.value)
    commit()
  })
  const noneWrap = el('label', 'flex items-center gap-1 text-[10px] text-slate-400')
  noneWrap.append(noneCb, document.createTextNode('none'))
  const ctl = el('div', 'flex items-center gap-2')
  ctl.append(swatch, noneWrap)
  return row(label, ctl)
}
